import axios from "axios";
import { newApiBaseUrl } from "../../axiosInstance";
import {
  extractPaymentStatus,
  getPaymentStatusText,
} from "../utils/paymentStatus";
import {
  extractTransactionType,
  getTransactionTypeText,
} from "../utils/transactionType";

const pgwHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `bearer ${localStorage.jwtToken}`,
  "X-Tenant": "hamazon",
});

export const TRANSACTIONS_BY_ORDERS_BATCH_SIZE = 100;

const chunkIds = (ids, size) => {
  const chunks = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
};

export const mergeTransactionsByOrdersMaps = (...maps) => {
  const merged = {};
  for (const map of maps) {
    if (!map || typeof map !== "object") continue;
    for (const [key, value] of Object.entries(map)) {
      if (Array.isArray(value)) merged[key] = value;
    }
  }
  return merged;
};

export const getTransactionsByOrder = (orderId) =>
  axios.get(`${newApiBaseUrl}/transactions`, {
    headers: pgwHeaders(),
    params: { orderId: String(orderId) },
  });

export const getTransactionsByDateRange = ({
  fromDate,
  toDate,
  limit = 100,
  cursor,
  status,
  orderId,
  transactionType,
}) =>
  axios.get(`${newApiBaseUrl}/transactions/by-date-range`, {
    headers: pgwHeaders(),
    params: {
      fromDate,
      toDate,
      limit,
      ...(cursor ? { cursor } : {}),
      ...(status ? { status } : {}),
      ...(orderId ? { orderId: String(orderId) } : {}),
      ...(transactionType ? { transactionType } : {}),
    },
  });

export const getTransactionsApiErrorMessage = (err) => {
  const data = err?.response?.data;
  const raw =
    (typeof data === "string" && data.trim()) ||
    (typeof data?.error === "string" && data.error.trim()) ||
    (typeof data?.message === "string" && data.message.trim()) ||
    "";

  if (/orderId must be|Missing.*orderId/i.test(raw)) {
    return "מספר הזמנה לא תקין. הזן מספר הזמנה המלא או נקה את השדה.";
  }
  if (/fromDate|toDate/i.test(raw)) {
    return "טווח התאריכים לא תקין. בדוק את תאריך ההתחלה והסיום.";
  }

  return raw || "שגיאה בטעינת תשלומים";
};

export const getTransactionsByOrders = (orderIds) =>
  axios.post(
    `${newApiBaseUrl}/transactions/by-orders`,
    {
      orderIds: orderIds.map((id) => String(id)),
    },
    { headers: pgwHeaders() },
  );

/** Fetches transactions in batches of at most 100 order IDs per request. */
export const getTransactionsByOrdersBatched = async (
  orderIds,
  batchSize = TRANSACTIONS_BY_ORDERS_BATCH_SIZE,
) => {
  const ids = orderIds.map((id) => String(id));
  if (!ids.length) return { data: {} };

  const chunks = chunkIds(ids, batchSize);
  const settled = await Promise.allSettled(
    chunks.map((chunk) =>
      getTransactionsByOrders(chunk).then((res) =>
        parseTransactionsByOrdersResponse(res.data),
      ),
    ),
  );

  const maps = [];
  const batchErrors = [];
  for (const result of settled) {
    if (result.status === "fulfilled") {
      maps.push(result.value);
    } else {
      batchErrors.push(result.reason);
    }
  }

  if (maps.length === 0) {
    throw batchErrors[0] ?? new Error("Failed to fetch transactions");
  }

  const data = mergeTransactionsByOrdersMaps(...maps);
  if (batchErrors.length > 0) {
    return { data, partialFailure: true, batchErrors };
  }
  return { data };
};

export const parseTransactionsResponse = (data) => {
  if (Array.isArray(data?.transactions)) return data.transactions;
  if (Array.isArray(data)) return data;
  return [];
};

/** PGW POST /transactions/by-orders: flat map orderId → transaction[]. */
export const parseTransactionsByOrdersResponse = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};
  if (data.byOrderId != null && typeof data.byOrderId === "object") {
    return data.byOrderId;
  }
  const map = {};
  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value)) map[key] = value;
  }
  return map;
};

export const formatSingleTransaction = (tx) => {
  if (!tx || typeof tx !== "object") return null;
  const status = extractPaymentStatus(tx);
  const transactionType = extractTransactionType(tx);
  const typeLabel = getTransactionTypeText(transactionType);
  const digits = tx.last_four_digits ?? tx.lastFourDigits;
  const sum = tx.sum ?? tx.amount;
  const parts = [];
  if (typeLabel) parts.push(typeLabel);
  if (status) parts.push(getPaymentStatusText(status));
  if (digits) parts.push(`\u200E****${digits}\u200E`);
  if (sum != null) parts.push(`₪${sum}`);
  return parts.length ? parts.join(" · ") : null;
};

export const getTransactionPaymentLabel = (transactions, fallback) => {
  if (!Array.isArray(transactions) || !transactions.length) {
    return fallback ? getPaymentStatusText(fallback) : null;
  }

  const latest =
    [...transactions].reverse().find((tx) => formatSingleTransaction(tx)) ??
    transactions[0];
  return (
    formatSingleTransaction(latest) ??
    (fallback ? getPaymentStatusText(fallback) : null) ??
    fallback ??
    null
  );
};
